// File: frontend/src/utils/leadScore.js
// Fuente única de verdad de la ESCALA de lead_score en el frontend.
// Espejo de backend/utils/leadScore.js — si cambias una, cambia la otra.
//
// La escala real es 0–10 (la fija `POST /api/ingest/lead-qualify`, que normaliza
// el 0–15 del bot SDR con round(raw/15*10)). Antes había vistas asumiendo 0–100
// (`{score}/100`, `>= 80`, buckets 0-40/41-60/61-100) y todo lead real caía en
// "Score Baixo". Todo el formateo de score pasa ahora por aquí.

export const LEAD_SCORE_MAX = 10;

// Alineadas con el `qualification_level` del bot SDR:
// baixo 3/15 -> 2 · medio 8/15 -> 5 · alto 11+/15 -> 7+
export const LEAD_SCORE_BANDS = [
  { key: 'low', label: 'Baixo', range: '0–3', min: 0, max: 3 },
  { key: 'medium', label: 'Médio', range: '4–6', min: 4, max: 6 },
  { key: 'high', label: 'Alto', range: '7–10', min: 7, max: LEAD_SCORE_MAX },
];

export const LEAD_SCORE_HIGH_MIN = 7;

// Paleta de bandas. Validada con el validador de la skill `dataviz`
// (banda de luminosidad, piso de croma, separación CVD y contraste sobre la
// superficie de la tarjeta) en modo claro y oscuro.
export const LEAD_SCORE_BAND_COLORS = {
  low: { light: '#e11d48', dark: '#e11d48' },
  medium: { light: '#f59e0b', dark: '#d97706' },
  high: { light: '#0d9488', dark: '#0d9488' },
};

// Escala cruda del ingest por email del SDR: parseSDR saca la "X/15" del asunto.
const SDR_RAW_MAX = 15;

/**
 * Normaliza un score a 0–10. Espejo de backend/utils/leadScore.js.
 *   <= 10   ya está en escala        -> tal cual
 *   11..15  crudo del SDR ("X/15")   -> round(n/15*10)
 *   > 15    legacy 0–100             -> round(n/10)
 */
export const normalizeLeadScore = (raw) => {
  if (raw === null || raw === undefined || raw === '') return null;
  const n = Number(raw);
  if (!Number.isFinite(n)) return null;
  let scaled = n;
  if (n > SDR_RAW_MAX) scaled = n / 10;
  else if (n > LEAD_SCORE_MAX) scaled = (n / SDR_RAW_MAX) * LEAD_SCORE_MAX;
  return Math.max(0, Math.min(LEAD_SCORE_MAX, Math.round(scaled)));
};

/** Banda ('low' | 'medium' | 'high') de un score, o null si no hay score. */
export const getScoreBand = (raw) => {
  const n = normalizeLeadScore(raw);
  if (n === null) return null;
  return LEAD_SCORE_BANDS.find((b) => n >= b.min && n <= b.max)?.key || 'low';
};

/** "7/10" — nunca escribas el denominador a mano. */
export const formatLeadScore = (raw) => `${normalizeLeadScore(raw) ?? 0}/${LEAD_SCORE_MAX}`;

/** Clases de badge (fondo + texto) por banda. */
export const getScoreColor = (raw) => {
  const band = getScoreBand(raw);
  if (band === 'high') return 'bg-green-100 text-green-800';
  if (band === 'medium') return 'bg-yellow-100 text-yellow-800';
  return 'bg-red-100 text-red-800';
};

/** Sólo color de texto (para iconos/estrella junto al número). */
export const getScoreTextColor = (raw) => {
  const band = getScoreBand(raw);
  if (band === 'high') return 'text-green-600';
  if (band === 'medium') return 'text-yellow-600';
  return 'text-red-600';
};

/** ¿Es un lead de banda alta? Un único umbral para KPIs y alertas. */
export const isHighScore = (raw) => getScoreBand(raw) === 'high';
